import { type FC, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router";

import { clsx } from "clsx";

import { useQuery } from "@apollo/client/react";

import { InboxStatusCountsDocument, MeDocument } from "@app_schema";

import { type Breadcrumb } from "@application/types/breadcrumb";

import { DASHBOARD_AGENTS_LINK } from "@application/navigation/dashboard_agents_link";
import { DASHBOARD_ORGANIZATIONS_LINK } from "@application/navigation/dashboard_organizations_link";
import { DASHBOARD_WORKFLOWS_LINK } from "@application/navigation/dashboard_workflows_link";

import { MenuProfile } from "./menu_profile";

// The status filters ARE the list-level nav: each item navigates to the
// projects list scoped to that summary category (the `?status=` param the list
// reads). "All" drops the param for a clean URL.
const STATUS_ITEMS = [
  { value: "all", label: "All" },
  { value: "needsUpload", label: "Needs records" },
  { value: "processing", label: "Processing" },
  { value: "readyForReview", label: "Ready" },
  { value: "archived", label: "Archived" },
] as const;

type StatusValue = (typeof STATUS_ITEMS)[number]["value"];

const RailLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="text-muted px-3 pb-1.5 text-[11px] font-semibold tracking-[0.08em] uppercase">
    {children}
  </div>
);

const RailItem: FC<{ to: string; active: boolean; children: ReactNode }> = ({
  to,
  active,
  children,
}) => (
  <Link
    to={to}
    className={clsx(
      "flex w-full items-center gap-2 rounded-xl px-3 py-[9px] text-left text-[13.5px]",
      active
        ? "surface-selected text-ink font-semibold"
        : "text-body hover:bg-quiet-hover hover:text-ink",
    )}
  >
    {children}
  </Link>
);

// Each builder place owns its child routes (which surface as in-page tabs), so
// the rail item stays active across the whole group, not just its index.
const BUILDER_LINKS: {
  link: Breadcrumb;
  key: "workflows" | "agents" | "organizations";
  paths: string[];
}[] = [
  {
    link: DASHBOARD_WORKFLOWS_LINK,
    key: "workflows",
    paths: [
      "/dashboard/workflows",
      "/dashboard/runs",
      "/dashboard/skills",
      "/dashboard/triggers",
    ],
  },
  {
    link: DASHBOARD_AGENTS_LINK,
    key: "agents",
    paths: [
      "/dashboard/agents",
      "/dashboard/conversations",
      "/dashboard/artifacts",
    ],
  },
  {
    link: DASHBOARD_ORGANIZATIONS_LINK,
    key: "organizations",
    paths: ["/dashboard/organizations"],
  },
];

// List-level navigation content (🅒). Org switcher on top, the CASES status nav
// (the filters are the nav), an admin-only BUILDER group. Body only — the
// width-animating shell and the shared settings gear live in DashboardRail.
export const CasesRail: FC = () => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { data: meData } = useQuery(MeDocument);
  // The counts no longer ride the list query, so nothing else refetches them.
  // Poll so archive / create / a run finishing can't leave the rail showing a
  // stale number in the primary nav. The rail stays mounted (folded) on
  // case-detail pages, so this keeps polling there too — a deliberate trade: one
  // cheap query/min that keeps the counts fresh for when the rail morphs back.
  const { data: countsData } = useQuery(InboxStatusCountsDocument, {
    pollInterval: 60_000,
  });

  const navigation = meData?.me?.navigation;
  const counts = countsData?.inboxStatusCounts;

  const onProjects = pathname === "/dashboard/projects";
  const activeStatus = (params.get("status") ?? "all") as StatusValue;

  const builderLinks = BUILDER_LINKS.filter(({ key }) => navigation?.[key]);

  return (
    <>
      <div className="border-b border-slate-200 pb-4">
        <MenuProfile />
      </div>

      <div className="mt-4 flex flex-col space-y-0.5">
        <RailLabel>Cases</RailLabel>
        {STATUS_ITEMS.map((item) => (
          <RailItem
            key={item.value}
            to={
              item.value === "all"
                ? "/dashboard/projects"
                : `/dashboard/projects?status=${item.value}`
            }
            active={onProjects && activeStatus === item.value}
          >
            {item.label}
            {counts && (
              <span className="text-muted ml-auto text-xs font-medium tabular-nums">
                {counts[item.value]}
              </span>
            )}
          </RailItem>
        ))}
      </div>

      {builderLinks.length > 0 && (
        <div className="mt-6 flex flex-col space-y-0.5">
          <RailLabel>Builder</RailLabel>
          {builderLinks.map(({ link, paths }) => (
            <RailItem
              key={link.to}
              to={link.to}
              active={paths.some((path) => pathname.startsWith(path))}
            >
              {link.name}
            </RailItem>
          ))}
        </div>
      )}
    </>
  );
};
